import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { In, Sel } from "@/pages/Projects";
import { Plus, Check, XCircle, Pencil, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "refacciones", label: "Refacciones" },
  { value: "consumibles", label: "Consumibles" },
  { value: "herramienta", label: "Herramienta" },
  { value: "servicios_externos", label: "Servicios externos" },
  { value: "equipo", label: "Equipo" },
  { value: "renta", label: "Renta" },
  { value: "otros", label: "Otros" },
];
const STATUS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "aprobado", label: "Aprobado" },
  { value: "pagado", label: "Pagado" },
  { value: "rechazado", label: "Rechazado" },
];
const METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "cheque", label: "Cheque" },
];
const STATUS_COLORS = { pendiente: "#fbbf24", aprobado: "#22d3ee", pagado: "#22c55e", rechazado: "#ef4444" };
const SOURCES = [
  { value: "empresa", label: "Pagó la empresa" },
  { value: "fondo_mecanico", label: "Fondo entregado al mecánico" },
  { value: "dinero_mecanico", label: "Dinero del mecánico (su bolsa)" },
];
const SOURCE_SHORT = { empresa: "EMPRESA", fondo_mecanico: "FONDO MEC.", dinero_mecanico: "SU DINERO" };
const SOURCE_COLORS = { empresa: "#94a3b8", fondo_mecanico: "#fbbf24", dinero_mecanico: "#ef4444" };
const PAGE_SIZE = 50;
const emptyForm = { description: "", category: "refacciones", amount: "", vendor: "", expense_date: todayLocal(), payment_method: "efectivo", invoice_number: "", notes: "", paid_source: "empresa", mechanic: "" };

export const WorkshopExpensesTab = () => {
  const { user } = useAuth();
  const canWrite = ["admin", "auxiliar", "mecanico"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sums, setSums] = useState(null);
  const [page, setPage] = useState(0);
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (fStatus) params.status = fStatus;
    if (fCategory) params.category = fCategory;
    if (fStart) params.start_date = fStart;
    if (fEnd) params.end_date = fEnd;
    const { data } = await api.get("/workshop-expenses", { params });
    setRows(data.items || []);
    setTotalCount(data.total || 0);
    setSums(data.sums || null);
  };
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [fStatus, fCategory, fStart, fEnd]);
  useEffect(() => { api.get("/workshop/mechanics").then(({ data }) => setMechanics(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [fStatus, fCategory, fStart, fEnd, page]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form, amount: Math.round(Number(form.amount) * 100) / 100, mechanic: form.mechanic?.trim() || null };
      if (editingId) await api.patch(`/workshop-expenses/${editingId}`, payload);
      else await api.post("/workshop-expenses", payload);
      toast.success(editingId ? "Gasto actualizado" : "Gasto registrado");
      setOpen(false); setEditingId(null); setForm(emptyForm);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/workshop-expenses/${id}`, { status }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este gasto del taller?")) return;
    try { await api.delete(`/workshop-expenses/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const startEdit = (e) => { setEditingId(e.id); setForm({ ...emptyForm, ...e, paid_source: e.paid_source || "empresa", mechanic: e.mechanic || "" }); setOpen(true); };
  const exportExcel = async () => {
    try {
      const params = {};
      if (fStatus) params.status = fStatus;
      if (fCategory) params.category = fCategory;
      if (fStart) params.start_date = fStart;
      if (fEnd) params.end_date = fEnd;
      const res = await api.get("/workshop-expenses/export/excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "gastos_taller.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo exportar"); }
  };
  const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap" data-testid="workshop-expenses-totals">
          <span className="text-[11px] font-mono text-slate-400">{totalCount} gasto{totalCount === 1 ? "" : "s"}</span>
          {sums && <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#22d3ee12", color: "#22d3ee", border: "1px solid #22d3ee40" }}>Total: <b>{fmtMXN(sums.total)}</b></span>}
          {sums?.pendiente > 0 && <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#fbbf2412", color: "#fbbf24", border: "1px solid #fbbf2440" }}>Pendiente: <b>{fmtMXN(sums.pendiente)}</b></span>}
          {sums?.pagado > 0 && <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#22c55e12", color: "#22c55e", border: "1px solid #22c55e40" }}>Pagado: <b>{fmtMXN(sums.pagado)}</b></span>}
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button data-testid="export-workshop-expenses-btn" onClick={exportExcel}
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
              style={{ border: "1px solid var(--green)", color: "var(--green)" }}>
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
            <button data-testid="add-workshop-expense-btn" onClick={() => { setEditingId(null); setForm(emptyForm); setOpen(true); }}
              className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
              <Plus className="w-4 h-4" /> Registrar gasto
            </button>
          </div>
        )}
      </div>

      <div className="panel p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Sel label="Estado" value={fStatus} onChange={setFStatus} options={[{ value: "", label: "Todos" }, ...STATUS]} testId="wexp-filter-status" />
        <Sel label="Categoría" value={fCategory} onChange={setFCategory} options={[{ value: "", label: "Todas" }, ...CATEGORIES]} testId="wexp-filter-category" />
        <In label="Desde" type="date" required={false} value={fStart} onChange={setFStart} testId="wexp-filter-start" />
        <In label="Hasta" type="date" required={false} value={fEnd} onChange={setFEnd} testId="wexp-filter-end" />
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="workshop-expenses-table">
            <thead className="panel-header">
              <tr className="text-left">
                {["Acciones", "Estado", "Categoría", "Descripción", "Proveedor", "Pagó", "Método", "Monto", "Fecha"].map((h, i) => (
                  <th key={h} className={`label-mono px-4 py-3 ${h === "Monto" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin gastos del taller.</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`wexp-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      {canWrite && e.status === "pendiente" && (
                        <>
                          <button data-testid={`wexp-approve-${e.id}`} onClick={() => setStatus(e.id, "aprobado")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }} title="Aprobar"><Check className="w-4 h-4" /></button>
                          <button data-testid={`wexp-reject-${e.id}`} onClick={() => setStatus(e.id, "rechazado")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }} title="Rechazar"><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      {canWrite && e.status === "aprobado" && (
                        <button data-testid={`wexp-pay-${e.id}`} onClick={() => setStatus(e.id, "pagado")} className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold hover:bg-white/5" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }} title="Marcar pagado">$ PAGAR</button>
                      )}
                      {canWrite && <button data-testid={`wexp-edit-${e.id}`} onClick={() => startEdit(e)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }} title="Editar"><Pencil className="w-3.5 h-3.5" /></button>}
                      {canDelete && <button data-testid={`wexp-delete-${e.id}`} onClick={() => remove(e.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }} title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ background: `${STATUS_COLORS[e.status]}1f`, color: STATUS_COLORS[e.status], border: `1px solid ${STATUS_COLORS[e.status]}55` }}>{e.status}</span>
                    {e.approved_by_name && <div className="text-[9px] text-slate-500 mt-0.5">{e.status === "rechazado" ? "✗" : "✓"} {e.approved_by_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">{catLabel(e.category)}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-100">{e.description}</span>
                    {e.invoice_number && <div className="text-[10px] text-slate-500">Folio {e.invoice_number}</div>}
                    {e.notes && <div className="text-[10px] text-slate-500 truncate max-w-[240px]" title={e.notes}>{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">{e.vendor || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest" style={{ color: SOURCE_COLORS[e.paid_source || "empresa"], border: `1px solid ${SOURCE_COLORS[e.paid_source || "empresa"]}55` }}>{SOURCE_SHORT[e.paid_source || "empresa"]}</span>
                    {e.mechanic && <div className="text-[10px] text-slate-400 mt-0.5">{e.mechanic}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 capitalize">{e.payment_method || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-100 whitespace-nowrap">{fmtMXN(e.amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300 whitespace-nowrap">{fmtDate(e.expense_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: "1px solid var(--border)" }} data-testid="workshop-expenses-pagination">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>← Anterior</button>
            <span className="text-xs font-mono text-slate-400">Página <b className="text-slate-100">{page + 1}</b> de {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}</span>
            <button disabled={(page + 1) * PAGE_SIZE >= totalCount} onClick={() => setPage(page + 1)} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>Siguiente →</button>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(3,6,14,0.8)", backdropFilter: "blur(4px)" }}>
          <div className="panel w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" data-testid="workshop-expense-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-slate-100">{editingId ? "Editar gasto del taller" : "Registrar gasto del taller"}</h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded-sm hover:bg-white/5 text-slate-400"><XCircle className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <In label="Descripción" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="wexp-description" />
              <div className="grid grid-cols-2 gap-3">
                <Sel label="Categoría" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="wexp-category" />
                <In label="Monto MXN" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="wexp-amount" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Fecha" type="date" value={form.expense_date} onChange={(v) => setForm({ ...form, expense_date: v })} testId="wexp-date" />
                <Sel label="Método de pago" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} options={METHODS} testId="wexp-method" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Proveedor" required={false} value={form.vendor} onChange={(v) => setForm({ ...form, vendor: v })} testId="wexp-vendor" />
                <In label="Folio / Factura" required={false} value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} testId="wexp-invoice" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Sel label="¿Quién pagó?" value={form.paid_source} onChange={(v) => setForm({ ...form, paid_source: v })} options={SOURCES} testId="wexp-paid-source" />
                {form.paid_source !== "empresa" ? (
                  <div>
                    <label className="label-mono block mb-1.5">Mecánico</label>
                    <select required value={form.mechanic} onChange={(e) => setForm({ ...form, mechanic: e.target.value })}
                      className="w-full input-cyber px-3 py-2 text-sm" data-testid="wexp-mechanic">
                      <option value="" style={{ background: "#0a0f1d" }}>— Selecciona —</option>
                      {form.mechanic && !mechanics.some((m) => m.name === form.mechanic) && (
                        <option value={form.mechanic} style={{ background: "#0a0f1d" }}>{form.mechanic} (sin registrar)</option>
                      )}
                      {mechanics.filter((m) => m.active !== false).map((m) => (
                        <option key={m.id} value={m.name} style={{ background: "#0a0f1d" }}>{m.name}{m.workshop_name ? ` · ${m.workshop_name}` : ""}</option>
                      ))}
                    </select>
                    {mechanics.length === 0 && <div className="text-[10px] mt-1" style={{ color: "#fbbf24" }}>Registra mecánicos en la pestaña Mecánicos.</div>}
                  </div>
                ) : <div />}
              </div>
              <div>
                <label className="label-mono block mb-1.5">Notas</label>
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" data-testid="wexp-save-btn" disabled={saving} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : editingId ? "Actualizar" : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopExpensesTab;
